/**
 * Color conversion and distance calculation utilities
 */

// RGB → Hex conversion
export const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
};

// Hex → RGB conversion
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
};

// RGB → HSL conversion
export const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
};

// Color distance calculation (RGB + HSL hybrid)
export const colorDistance = (r1, g1, b1, r2, g2, b2) => {
  const dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
  const rgbDist = Math.sqrt(dr * dr + dg * dg + db * db);

  const hsl1 = rgbToHsl(r1, g1, b1);
  const hsl2 = rgbToHsl(r2, g2, b2);

  if (hsl1.s < 0.15 || hsl2.s < 0.15) return rgbDist;

  const satDiff = Math.abs(hsl1.s - hsl2.s);
  if (satDiff > 0.3) return rgbDist;

  let hueDiff = Math.abs(hsl1.h - hsl2.h);
  if (hueDiff > 0.5) hueDiff = 1 - hueDiff;

  const hueBonus = hueDiff < 0.05 ? 0.85 : 1.0;
  return rgbDist * hueBonus;
};

/**
 * Image coordinate calculation helper
 * Calculates the actual display area of an object-contain image
 */
export const getImageDisplayMetrics = (imageEl) => {
  const rect = imageEl.getBoundingClientRect();
  const nw = imageEl.naturalWidth;
  const nh = imageEl.naturalHeight;
  const cRatio = rect.width / rect.height;
  const iRatio = nw / nh;

  let dw, dh, ox, oy;
  if (iRatio > cRatio) {
    dw = rect.width; dh = rect.width / iRatio; ox = 0; oy = (rect.height - dh) / 2;
  } else {
    dh = rect.height; dw = rect.height * iRatio; ox = (rect.width - dw) / 2; oy = 0;
  }

  return { displayWidth: dw, displayHeight: dh, offsetX: ox, offsetY: oy, naturalWidth: nw, naturalHeight: nh, rect };
};
